import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { IRole } from '../../../../models/settings.models';
import { requiredTrimValidator } from 'src/app/shared/validators/fields-validators';
import { SharedDialogOptions } from 'src/app/ui/shared-dialog/shared-dialog.component';

export interface RoleFormDialogData {
  role: IRole | null;
}

export interface RoleFormPayload {
  name: string;
}

@Component({
  selector: 'app-role-form-dialog',
  templateUrl: './role-form-dialog.component.html',
  styleUrls: ['./role-form-dialog.component.scss'],
})
export class RoleFormDialogComponent implements OnInit {
  @Output() save = new EventEmitter<RoleFormPayload>();

  role: IRole | null = null;
  readonly roleNameControl = new FormControl('', requiredTrimValidator());
  isSubmitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: RoleFormDialogData,
    private readonly dialogRef: MatDialogRef<RoleFormDialogComponent>,
    private readonly translateService: TranslateService,
  ) {
    this.role = data?.role ?? null;
  }

  ngOnInit(): void {
    this.roleNameControl.setValue(this.role?.name ?? '');
  }

  get isEditMode(): boolean {
    return !!this.role;
  }

  get dialogTitle(): string {
    return this.isEditMode
      ? `${this.translateService.instant('common.edit')} ${this.translateService.instant('settings.role')}`
      : `${this.translateService.instant('common.add')} ${this.translateService.instant('settings.role')}`;
  }

  get saveLabel(): string {
    return this.isEditMode
      ? this.translateService.instant('common.save')
      : this.translateService.instant('common.add');
  }

  get isFormInvalid(): boolean {
    return this.roleNameControl.invalid;
  }

  get dialogOptions(): SharedDialogOptions {
    return {
      size: 'medium',
      cancelText: this.translateService.instant('common.cancel'),
      customConfirmButton: true,
      hideConfirmButton: true,
    };
  }

  submitForm(): void {
    this.roleNameControl.markAsTouched();
    if (this.isFormInvalid || this.isSubmitting) return;

    const name = (this.roleNameControl.value ?? '').trim();
    if (!name) return;

    this.isSubmitting = true;
    this.save.emit({ name });
  }

  close(): void {
    if (this.isSubmitting) return;
    this.dialogRef.close();
  }

  setSubmitting(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
  }
}
